import { useState } from 'react';
import { LuCheck, LuChevronsUpDown, LuFilter, LuX } from 'react-icons/lu';

import { GlobalAccount, GUID, OpportunityStatus } from '@ndvr/customer-api';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/utils/cn';
import { getHouseholdName, titleCase } from 'utils';
import { useClientsAndProspects } from 'views/household/household-queries';

import { useOpportunityViewState } from '../opportunity-state';

export const OpportunityFilter = () => {
    const { opportunityViewState, setOpportunityViewState } = useOpportunityViewState();
    const { data: households = [] } = useClientsAndProspects();
    const [isOpen, setIsOpen] = useState(false);
    const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
    const [householdPopoverOpen, setHouseholdPopoverOpen] = useState(false);
    const [minValue, setMinValue] = useState(opportunityViewState.minimumValue?.toString() || '');

    const statusOptions = Object.values(OpportunityStatus).map((status) => ({
        value: status,
        label: titleCase(status.replace(/_/g, ' ')),
    }));

    const handleStatusToggle = (status: OpportunityStatus) => {
        const currentSelected = opportunityViewState.selectedStatuses;
        const isSelected = currentSelected.includes(status);
        const newSelected = isSelected ? currentSelected.filter((s) => s !== status) : [...currentSelected, status];
        setOpportunityViewState({ selectedStatuses: newSelected });
    };

    const handleHouseholdToggle = (householdGUID: GUID<GlobalAccount>) => {
        const currentSelected = opportunityViewState.selectedHouseholds;
        const isSelected = currentSelected.includes(householdGUID);
        const newSelected = isSelected
            ? currentSelected.filter((p) => p !== householdGUID)
            : [...currentSelected, householdGUID];
        setOpportunityViewState({ selectedHouseholds: newSelected });
    };

    const handleMinValueChange = (value: string) => {
        setMinValue(value);
        setOpportunityViewState({
            minimumValue: value ? parseFloat(value) : null,
        });
    };

    const handleClearAll = () => {
        setOpportunityViewState({
            selectedStatuses: [],
            selectedHouseholds: [],
            minimumValue: null,
        });
        setMinValue('');
    };

    const removeStatus = (status: OpportunityStatus) => {
        setOpportunityViewState({
            selectedStatuses: opportunityViewState.selectedStatuses.filter((s) => s !== status),
        });
    };

    const removeHousehold = (householdGUID: GUID<GlobalAccount>) => {
        setOpportunityViewState({
            selectedHouseholds: opportunityViewState.selectedHouseholds.filter((p) => p !== householdGUID),
        });
    };

    const getTotalFilterCount = () => {
        return (
            opportunityViewState.selectedStatuses.length +
            opportunityViewState.selectedHouseholds.length +
            (opportunityViewState.minimumValue ? 1 : 0)
        );
    };

    const totalFilterCount = getTotalFilterCount();

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="outline2" size="sm" className="flex items-center space-x-1">
                    <LuFilter className="mr-1 h-4 w-4" />
                    <span>Filter</span>
                    {totalFilterCount > 0 && (
                        <div className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#946A3E] text-xs text-white">
                            {totalFilterCount}
                        </div>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-96 p-0" align="start">
                <div className="flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b p-4">
                        <Label className="text-sm font-semibold">Filters</Label>
                        <Button variant="ghost" size="sm" onClick={handleClearAll}>
                            Clear all
                        </Button>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                        <div className="space-y-4">
                            {/* Status Filter */}
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Status</Label>
                                <Popover open={statusPopoverOpen} onOpenChange={setStatusPopoverOpen}>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" role="combobox" className="w-full justify-between">
                                            <span className="truncate">
                                                {opportunityViewState.selectedStatuses.length > 0
                                                    ? `${opportunityViewState.selectedStatuses.length} selected`
                                                    : 'Select statuses'}
                                            </span>
                                            <LuChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                                        <Command>
                                            <CommandInput placeholder="Search statuses..." />
                                            <CommandList>
                                                <CommandEmpty>No status found.</CommandEmpty>
                                                {statusOptions.map((status) => (
                                                    <CommandItem
                                                        key={status.value}
                                                        value={status.label}
                                                        onSelect={() => handleStatusToggle(status.value)}
                                                    >
                                                        <LuCheck
                                                            className={cn(
                                                                'mr-2 h-4 w-4',
                                                                opportunityViewState.selectedStatuses.includes(
                                                                    status.value,
                                                                )
                                                                    ? 'opacity-100'
                                                                    : 'opacity-0',
                                                            )}
                                                        />
                                                        {status.label}
                                                    </CommandItem>
                                                ))}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                {opportunityViewState.selectedStatuses.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                        {opportunityViewState.selectedStatuses.map((status) => {
                                            const statusLabel = statusOptions.find((s) => s.value === status)?.label;
                                            return (
                                                <Badge key={status} variant="secondary" className="text-xs">
                                                    {statusLabel}
                                                    <button
                                                        className="ml-1 rounded-full hover:bg-gray-300"
                                                        onClick={() => removeStatus(status)}
                                                    >
                                                        <LuX className="h-3 w-3" />
                                                    </button>
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Minimum Value Filter */}
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Minimum Value ($)</Label>
                                <Input
                                    type="number"
                                    placeholder="Enter minimum value"
                                    value={minValue}
                                    onChange={(e) => handleMinValueChange(e.target.value)}
                                    min="0"
                                    step="1000"
                                    className="h-9"
                                />
                            </div>

                            {/* Household Filter */}
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Household</Label>
                                <Popover open={householdPopoverOpen} onOpenChange={setHouseholdPopoverOpen}>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" role="combobox" className="w-full justify-between">
                                            <span className="truncate">
                                                {opportunityViewState.selectedHouseholds.length > 0
                                                    ? `${opportunityViewState.selectedHouseholds.length} selected`
                                                    : 'Select households'}
                                            </span>
                                            <LuChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                                        <Command>
                                            <CommandInput placeholder="Search households..." />
                                            <CommandList>
                                                <CommandEmpty>No household found.</CommandEmpty>
                                                {households.map((household: any) => (
                                                    <CommandItem
                                                        key={household.globalAccountGUID}
                                                        value={getHouseholdName(household)}
                                                        onSelect={() =>
                                                            handleHouseholdToggle(household.globalAccountGUID)
                                                        }
                                                    >
                                                        <LuCheck
                                                            className={cn(
                                                                'mr-2 h-4 w-4',
                                                                opportunityViewState.selectedHouseholds.includes(
                                                                    household.globalAccountGUID,
                                                                )
                                                                    ? 'opacity-100'
                                                                    : 'opacity-0',
                                                            )}
                                                        />
                                                        {getHouseholdName(household)}
                                                    </CommandItem>
                                                ))}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                {opportunityViewState.selectedHouseholds.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                        {opportunityViewState.selectedHouseholds.map((householdGUID) => {
                                            const household = households.find(
                                                (p: any) => p.globalAccountGUID === householdGUID,
                                            );
                                            return (
                                                <Badge key={householdGUID} variant="secondary" className="text-xs">
                                                    {household ? getHouseholdName(household) : householdGUID}
                                                    <button
                                                        className="ml-1 rounded-full hover:bg-gray-300"
                                                        onClick={() => removeHousehold(householdGUID)}
                                                    >
                                                        <LuX className="h-3 w-3" />
                                                    </button>
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
